import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import Reveal from "@/components/Reveal";
import RENav from "./RENav";
import { sendContact } from "@/lib/api";
import LegalLinks from "@/components/LegalLinks";

export default function InvestmentPage() {
  const { t } = useLang();
  const inv = t.investment;
  const [form, setForm] = useState({ name: "", email: "", role: "", verify: "" });
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await sendContact("investment", form.name, form.email, { Role: form.role, "Wants to verify": form.verify });
      setStatus("sent");
      setForm({ name: "", email: "", role: "", verify: "" });
    } catch (err) {
      setStatus("error");
    }
  };

  const inputCls = "bg-transparent border border-charcoal/35 text-charcoal placeholder:text-charcoal/45 focus:border-brass px-3.5 py-3 font-body text-sm transition-colors";

  return (
    <main className="bg-ink text-paper" data-testid="investment-page">
      <RENav
        theme="dark"
        word={t.nav.investment}
        links={[
          { href: "#thesis", label: inv.navThesis },
          { href: "#track", label: inv.navTrack },
          { href: "#contact", label: inv.navContact },
        ]}
      />

      <section className="relative pt-28 md:pt-36 pb-12 md:pb-16">
        <div className="mx-auto max-w-[1180px] px-6 md:px-8">
          <div className="grid grid-cols-12 gap-6 items-end">
            <div className="col-span-12 md:col-span-7">
              <Reveal>
                <span className="font-mono-data text-[0.62rem] text-brass block mb-5">{inv.kicker}</span>
                <h1 className="font-heading font-semibold text-[2.5rem] md:text-[4.2rem] leading-[1.02] max-w-[16ch]">{inv.h1}</h1>
                <p className="text-[1.1rem] leading-[1.6] text-paper/80 max-w-[50ch] mt-6 mb-8">{inv.intro}</p>
                <a href="#contact" data-testid="investment-hero-cta" className="inline-flex items-center gap-2 font-mono-data text-[0.66rem] bg-paper text-ink px-5 py-3.5 hover:bg-brass transition-colors">
                  {inv.cta} <ArrowRight className="h-3.5 w-3.5" />
                </a>
              </Reveal>
            </div>
            <div className="col-span-12 md:col-span-5">
              <Reveal delay={0.15}>
                <div className="relative w-full h-[34vh] md:h-[40vh] overflow-hidden">
                  <img src="/assets/re-exterior.webp" alt="Investment property architecture" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-ink/30" />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section id="thesis" className="py-16 md:py-20 border-t border-paper/15">
        <div className="mx-auto max-w-[1180px] px-6 md:px-8">
          <Reveal><h2 className="font-heading font-semibold italic text-[1.7rem] md:text-[2.4rem] mb-10">{inv.thesisTitle}</h2></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
            <Reveal>
              <p className="text-[1.02rem] leading-[1.6] text-paper/80 mb-4">{inv.thesisP1}</p>
              <p className="text-[1.02rem] leading-[1.6] text-paper/80">{inv.thesisP2}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="border-l-2 border-brass pl-6">
                {inv.specs.map((s, i) => (
                  <div key={s.k} className={`flex justify-between items-baseline gap-4 py-4 ${i < inv.specs.length - 1 ? "border-b border-paper/12" : ""}`} data-testid={`investment-spec-${i}`}>
                    <span className="font-mono-data text-[0.66rem] text-paper/55">{s.k}</span>
                    <span className="text-right text-[0.92rem] text-paper/85">{s.v}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section id="track" className="py-16 md:py-20 border-t border-paper/15">
        <div className="mx-auto max-w-[1180px] px-6 md:px-8">
          <Reveal className="flex items-baseline justify-between gap-6 flex-wrap mb-10">
            <h2 className="font-heading font-semibold italic text-[1.7rem] md:text-[2.4rem]">{inv.trackTitle}</h2>
            <span className="font-mono-data text-[0.62rem] text-paper/50">{inv.trackMeta}</span>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="border border-dashed border-paper/30 px-8 py-12 text-center" data-testid="investment-track-box">
              <h3 className="font-heading italic font-semibold text-[1.5rem] mb-3">{inv.trackBoxTitle}</h3>
              <p className="text-paper/70 max-w-[48ch] mx-auto leading-[1.6]">{inv.trackBoxText}</p>
              <span className="font-mono-data text-[0.62rem] text-paper/50 block mt-6">{inv.trackBoxNote}</span>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="verify" className="py-16 md:py-20 border-t border-paper/15">
        <div className="mx-auto max-w-[1180px] px-6 md:px-8">
          <Reveal><h2 className="font-heading font-semibold italic text-[1.7rem] md:text-[2.4rem] mb-10">{inv.verifyTitle}</h2></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {inv.verify.map((card, i) => (
              <Reveal key={card.label} delay={i * 0.07}>
                <article className="border-t-2 border-brass pt-4" data-testid={`investment-verify-${i}`}>
                  <span className="font-mono-data text-[0.62rem] text-brass block mb-3">{card.label}</span>
                  <h3 className="font-heading font-semibold text-[1.2rem] mb-2.5">{card.title}</h3>
                  <p className="text-[0.95rem] leading-[1.6] text-paper/75">{card.text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="py-16 md:py-20 border-t border-paper/15">
        <div className="mx-auto max-w-[1180px] px-6 md:px-8">
          <Reveal>
            <div className="grid grid-cols-1 md:grid-cols-[1.1fr_0.9fr] gap-0 bg-stone text-charcoal overflow-hidden">
              <div className="relative p-8 md:p-12 flex flex-col justify-center">
                <img src="/assets/medallion.png" alt="" className="absolute top-8 right-8 w-20 opacity-15 rounded-full" />
                <h2 className="font-heading font-semibold italic text-[1.7rem] md:text-[2.2rem] text-charcoal mb-3">{inv.contactTitle}</h2>
                <p className="text-charcoal/75 max-w-[42ch]">{inv.contactText}</p>
              </div>
              <div className="p-8 md:p-12 bg-stone-soft">
                {status === "sent" ? (
                  <p data-testid="investment-form-success" className="font-body text-sm text-charcoal border border-brass/50 px-4 py-6">{t.form.sent}</p>
                ) : (
                  <form className="grid gap-3" onSubmit={submit} data-testid="investment-contact-form">
                    <input data-testid="investment-form-name" type="text" required placeholder={t.form.name} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
                    <input data-testid="investment-form-email" type="email" required placeholder={t.form.email} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
                    <select data-testid="investment-form-role" required value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={`${inputCls} appearance-none`}>
                      <option value="" disabled style={{ color: "#000" }}>{inv.roleLabel}</option>
                      {inv.roles.map((r) => <option key={r} value={r} style={{ color: "#000" }}>{r}</option>)}
                    </select>
                    <input data-testid="investment-form-verify" type="text" placeholder={inv.verifyField} value={form.verify} onChange={(e) => setForm({ ...form, verify: e.target.value })} className={inputCls} />
                    <button data-testid="investment-form-submit" type="submit" disabled={status === "sending"} className="justify-self-start mt-1 font-mono-data text-[0.66rem] px-5 py-3 transition-colors disabled:opacity-50 bg-ink text-paper hover:bg-crimson">
                      {status === "sending" ? t.form.sending : inv.contactButton}
                    </button>
                    {status === "error" && <p data-testid="investment-form-error" className="text-[0.75rem] text-crimson">{t.form.error}</p>}
                    <p className="text-[0.7rem] mt-1 text-charcoal/55">{inv.contactNote}</p>
                  </form>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <footer className="py-10">
        <div className="mx-auto max-w-[1180px] px-6 md:px-8 flex flex-wrap items-end justify-between lg:grid lg:grid-cols-[1fr_auto_1fr] gap-6">
          <div>
            <p className="font-heading font-semibold uppercase tracking-[0.16em] text-paper text-[1.05rem]">{inv.footerBrand}</p>
            <p className="font-mono-data text-[0.6rem] tracking-[0.2em] text-paper/50 mt-1.5">{t.common.company}</p>
          </div>
          <LegalLinks className="font-mono-data text-[0.6rem] tracking-[0.2em] text-paper/50 order-last w-full text-center lg:order-none lg:w-auto" />
          <span className="lg:justify-self-end font-mono-data text-[0.6rem] tracking-[0.2em] text-paper/50">© {new Date().getFullYear()} {inv.footerBrand}</span>
        </div>
      </footer>
    </main>
  );
}
